import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ProgramScopeType, ProgramScopedContentRow, ProgramScopedContentRowUpsert } from '../types/domain';
import {
  deleteProgramScopedContentRowsByIds,
  saveProgramScopedContentRowsDiff,
  upsertProgramScopedContentRows,
} from '../services/programScopedContentService';
import {
  filterProgramScopedContentRowsByTermNumber,
  saveProgramScopedContentRowsCache,
} from '../utils/programScopedContentRowsCache';
import { recordDevLocalStorageProgramCacheUse } from '../utils/devNetworkTransferMonitor';
import {
  isProgramScopedScopeServerValidated,
  markProgramScopedScopeServerValidated,
  revokeProgramScopedScopeServerValidated,
} from '../utils/programScopedRowsServerValidatedSession';
import {
  readValidScopedContentRowsFromLocalCache,
  scopedContentLsRowsMatchScopeKey,
  sliceScopedRowsFromGradeTermLocalCache,
} from '../utils/programScopedContentLsBridge';
import { invalidateProgramScopedContentRowsQuery, setProgramScopedContentRowsQueryData } from '../query/cacheUpdates';
import { schoolKeys } from '../query/keys';
import { useProgramScopedContentByScopeQuery } from '../query/hooks/useProgramScopedContentByScopeQuery';
import {
  PROGRAM_PROGRESS_SAVED_EVENT,
  type ProgramProgressSavedDetail,
} from '../utils/programProgressSavedEvents';

interface UseProgramScopedContentRowsParams {
  workspaceId: string;
  academicYear: number;
  termNumber: 1 | 2 | null;
  gradeId: string;
  scopeType: ProgramScopeType;
  scopeKey: string;
  refreshKey?: number;
  isActive?: boolean;
}

export interface ProgramScopedContentRowsState {
  loading: boolean;
  error: string | null;
  rows: ProgramScopedContentRow[];
  upsertRows: (rows: ProgramScopedContentRowUpsert[]) => Promise<ProgramScopedContentRow[]>;
  /**
   * 표 전체 스냅샷을 저장본으로: 기준본(화면이 불러온 서버 목록)과의 차이(변경·추가·삭제)만 반영하고
   * 목록을 결과로 교체한다.
   */
  saveSnapshotRows: (
    snapshot: ProgramScopedContentRowUpsert[],
    baselineRows: readonly ProgramScopedContentRow[]
  ) => Promise<ProgramScopedContentRow[]>;
  deleteRowsByIds: (ids: string[]) => Promise<void>;
}

function scopeIdentity(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2 | null,
  gradeId: string,
  scopeType: ProgramScopeType,
  scopeKey: string
): string {
  return `${workspaceId}|${academicYear}|${termNumber ?? ''}|${gradeId}|${scopeType}|${scopeKey}`;
}

export function useProgramScopedContentRows(params: UseProgramScopedContentRowsParams): ProgramScopedContentRowsState {
  const { workspaceId, academicYear, termNumber, gradeId, scopeType, scopeKey, refreshKey = 0, isActive = true } =
    params;
  const queryClient = useQueryClient();
  const lastHandledRefreshKeyRef = React.useRef(refreshKey);
  const lastRefreshInvalidateRef = React.useRef(refreshKey);
  const refreshFetchSeenKeyRef = React.useRef(0);
  const rowsOriginScopeIdRef = React.useRef<string>('');
  const [fetchFromServer, setFetchFromServer] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [rows, setRows] = React.useState<ProgramScopedContentRow[]>(() => {
    if (!workspaceId || !gradeId || !termNumber || !scopeKey) return [];
    return readValidScopedContentRowsFromLocalCache(
      workspaceId,
      academicYear,
      termNumber,
      gradeId,
      scopeType,
      scopeKey
    ).rows;
  });
  const [loading, setLoading] = React.useState(() => {
    if (!workspaceId || !gradeId || !termNumber || !scopeKey) return false;
    return !readValidScopedContentRowsFromLocalCache(
      workspaceId,
      academicYear,
      termNumber,
      gradeId,
      scopeType,
      scopeKey
    ).found;
  });

  const tn = termNumber as 1 | 2;
  const scopeReady = isActive && !!workspaceId && !!gradeId && !!termNumber && !!scopeKey;
  const mustRefreshFromServer = refreshKey !== lastHandledRefreshKeyRef.current;

  const query = useProgramScopedContentByScopeQuery({
    workspaceId,
    academicYear,
    termNumber: tn,
    gradeId,
    scopeType,
    scopeKey,
    enabled: scopeReady && (fetchFromServer || mustRefreshFromServer),
    forceRefresh: mustRefreshFromServer,
  });

  React.useLayoutEffect(() => {
    if (!isActive) return;
    if (!workspaceId || !gradeId || !termNumber || !scopeKey) {
      rowsOriginScopeIdRef.current = '';
      setRows([]);
      setError(null);
      setLoading(false);
      setFetchFromServer(false);
      return;
    }
    const id = scopeIdentity(workspaceId, academicYear, termNumber, gradeId, scopeType, scopeKey);
    // keep-alive 복귀: 동일 스코프 메모리 행이 있으면 LS miss여도 지우지 않음
    if (!mustRefreshFromServer && rowsOriginScopeIdRef.current === id) {
      setError(null);
      setLoading(false);
      setFetchFromServer(false);
      return;
    }
    let preferServerOverTermSlice = false;
    const localCache = readValidScopedContentRowsFromLocalCache(
      workspaceId,
      academicYear,
      termNumber,
      gradeId,
      scopeType,
      scopeKey
    );
    if (localCache.found && !mustRefreshFromServer) {
      rowsOriginScopeIdRef.current = id;
      setRows(localCache.rows);
      setError(null);
      setLoading(false);
      setFetchFromServer(false);
      recordDevLocalStorageProgramCacheUse('프로그램 스코프행');
      return;
    }
    if (localCache.found && mustRefreshFromServer) {
      rowsOriginScopeIdRef.current = id;
      /** replace 저장 직후 LS 캐시는 이미 갱신됐지만 state는 예전 행 목록을 유지할 수 있다 — 즉시 반영 */
      setRows(localCache.rows);
      setError(null);
      setLoading(false);
      setFetchFromServer(true);
      recordDevLocalStorageProgramCacheUse('프로그램 스코프행');
      return;
    }
    if (isProgramScopedScopeServerValidated(id)) {
      revokeProgramScopedScopeServerValidated(id);
      preferServerOverTermSlice = true;
    }
    const fromTerm = sliceScopedRowsFromGradeTermLocalCache(
      workspaceId,
      academicYear,
      termNumber,
      gradeId,
      scopeType,
      scopeKey
    );
    if (
      !mustRefreshFromServer &&
      !preferServerOverTermSlice &&
      fromTerm.length > 0 &&
      scopedContentLsRowsMatchScopeKey(fromTerm, scopeKey)
    ) {
      rowsOriginScopeIdRef.current = id;
      setRows(fromTerm);
      setError(null);
      setLoading(false);
      setFetchFromServer(false);
      saveProgramScopedContentRowsCache(
        workspaceId,
        academicYear,
        termNumber,
        gradeId,
        scopeType,
        scopeKey,
        fromTerm
      );
      return;
    }
    if (mustRefreshFromServer && rowsOriginScopeIdRef.current === id) {
      setFetchFromServer(true);
      setLoading(false);
      return;
    }
    rowsOriginScopeIdRef.current = '';
    setRows([]);
    setError(null);
    setLoading(true);
    setFetchFromServer(true);
  }, [
    academicYear,
    gradeId,
    isActive,
    mustRefreshFromServer,
    scopeKey,
    scopeType,
    termNumber,
    workspaceId,
  ]);

  React.useEffect(() => {
    if (!scopeReady || refreshKey <= lastRefreshInvalidateRef.current) return;
    lastRefreshInvalidateRef.current = refreshKey;
    invalidateProgramScopedContentRowsQuery(workspaceId, academicYear, tn, gradeId, scopeType, scopeKey);
    setFetchFromServer(true);
    const id = scopeIdentity(workspaceId, academicYear, termNumber, gradeId, scopeType, scopeKey);
    const localCache = readValidScopedContentRowsFromLocalCache(
      workspaceId,
      academicYear,
      termNumber,
      gradeId,
      scopeType,
      scopeKey
    );
    if (localCache.found) {
      rowsOriginScopeIdRef.current = id;
      setRows(localCache.rows);
      setLoading(false);
    } else if (rowsOriginScopeIdRef.current !== id) {
      setLoading(true);
    }
  }, [academicYear, gradeId, refreshKey, scopeKey, scopeReady, scopeType, tn, workspaceId]);

  /** cache 저장 직후 LS는 갱신됐지만 refreshKey를 올리지 않을 때 훅 state를 맞춘다 */
  React.useEffect(() => {
    if (!scopeReady || !workspaceId || !gradeId || !termNumber || !scopeKey) return undefined;
    const onProgramSaved = (event: Event) => {
      const d = (event as CustomEvent<ProgramProgressSavedDetail>).detail;
      if (!d?.workspaceId || !d.gradeId) return;
      if (d.workspaceId !== workspaceId || d.gradeId !== gradeId) return;
      if (d.academicYear !== academicYear || d.termNumber !== termNumber) return;
      if ((d.uiRefreshMode ?? 'full') !== 'cache') return;
      const localCache = readValidScopedContentRowsFromLocalCache(
        workspaceId,
        academicYear,
        termNumber,
        gradeId,
        scopeType,
        scopeKey
      );
      if (!localCache.found) return;
      const id = scopeIdentity(workspaceId, academicYear, termNumber, gradeId, scopeType, scopeKey);
      rowsOriginScopeIdRef.current = id;
      setRows(localCache.rows);
      setError(null);
      setLoading(false);
      setFetchFromServer(false);
      recordDevLocalStorageProgramCacheUse('프로그램 스코프행·저장후동기화');
    };
    window.addEventListener(PROGRAM_PROGRESS_SAVED_EVENT, onProgramSaved as EventListener);
    return () => window.removeEventListener(PROGRAM_PROGRESS_SAVED_EVENT, onProgramSaved as EventListener);
  }, [academicYear, gradeId, scopeKey, scopeReady, scopeType, termNumber, workspaceId]);

  React.useEffect(() => {
    if (!scopeReady || !fetchFromServer) return;
    if (query.isLoading || query.isFetching) {
      if (mustRefreshFromServer && query.isFetching) refreshFetchSeenKeyRef.current = refreshKey;
      return;
    }
    if (mustRefreshFromServer && refreshFetchSeenKeyRef.current !== refreshKey) return;
    if (query.error) {
      setError(query.error instanceof Error ? query.error.message : String(query.error));
      if (!mustRefreshFromServer) {
        setRows([]);
        rowsOriginScopeIdRef.current = '';
      }
      setLoading(false);
      return;
    }
    if (query.data == null) return;
    const id = scopeIdentity(workspaceId, academicYear, termNumber, gradeId, scopeType, scopeKey);
    if (!scopedContentLsRowsMatchScopeKey(query.data, scopeKey)) {
      setError('서버에서 불러온 프로그램 행의 스코프가 일치하지 않습니다.');
      setRows([]);
      rowsOriginScopeIdRef.current = '';
      setLoading(false);
      return;
    }
    rowsOriginScopeIdRef.current = id;
    setRows(query.data);
    markProgramScopedScopeServerValidated(id);
    lastHandledRefreshKeyRef.current = refreshKey;
    setError(null);
    setLoading(false);
    setFetchFromServer(false);
  }, [
    academicYear,
    fetchFromServer,
    gradeId,
    mustRefreshFromServer,
    query.data,
    query.error,
    query.isFetching,
    query.isLoading,
    refreshKey,
    scopeKey,
    scopeReady,
    scopeType,
    termNumber,
    workspaceId,
  ]);

  React.useEffect(() => {
    if (!scopeReady || loading || error) return;
    const id = scopeIdentity(workspaceId, academicYear, termNumber, gradeId, scopeType, scopeKey);
    if (rowsOriginScopeIdRef.current !== id) return;
    const rowsForTerm = filterProgramScopedContentRowsByTermNumber(rows, termNumber);
    if (!scopedContentLsRowsMatchScopeKey(rowsForTerm, scopeKey)) return;
    /**
     * 훅 state가 아직 빈데 LS에는 가져오기·replace 저장본이 있으면 덮어쓰지 않는다.
     * (저장 직후 dirty workspace 제거 → 빈 훅이 rich 캐시를 지워 표가 비는 문제)
     */
    if (!rowsForTerm.length) {
      const existing = readValidScopedContentRowsFromLocalCache(
        workspaceId,
        academicYear,
        termNumber,
        gradeId,
        scopeType,
        scopeKey
      );
      if (existing.found && existing.rows.length > 0) return;
    }
    saveProgramScopedContentRowsCache(workspaceId, academicYear, termNumber, gradeId, scopeType, scopeKey, rowsForTerm);
  }, [academicYear, error, gradeId, loading, rows, scopeKey, scopeReady, scopeType, termNumber, workspaceId]);

  const upsertRows = React.useCallback(
    async (upserts: ProgramScopedContentRowUpsert[]): Promise<ProgramScopedContentRow[]> => {
      if (!upserts.length || !termNumber) return [];
      try {
        const saved = await upsertProgramScopedContentRows(upserts);
        /**
         * 표 전체 replace 저장은 merge로 이전 행을 남기지 않는다.
         * (삭제 후 merge 잔존 → LS/표시 캐시가 되살아나 내보내기 등에서 복원되는 문제)
         */
        const sorted = [...saved].sort(
          (a, b) => (a.order_index - b.order_index) || (a.created_at ?? '').localeCompare(b.created_at ?? '')
        );
        saveProgramScopedContentRowsCache(
          workspaceId,
          academicYear,
          termNumber,
          gradeId,
          scopeType,
          scopeKey,
          sorted
        );
        setProgramScopedContentRowsQueryData(
          workspaceId,
          academicYear,
          tn,
          gradeId,
          scopeType,
          scopeKey,
          sorted
        );
        queryClient.setQueryData(
          schoolKeys.programScopedContentRows(
            workspaceId,
            academicYear,
            tn,
            gradeId,
            scopeType,
            scopeKey
          ),
          sorted
        );
        setRows(sorted);
        setError(null);
        return sorted;
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        throw e instanceof Error ? e : new Error(String(e));
      }
    },
    [academicYear, gradeId, queryClient, scopeKey, scopeType, termNumber, tn, workspaceId]
  );

  const saveSnapshotRows = React.useCallback(
    async (
      snapshot: ProgramScopedContentRowUpsert[],
      baselineRows: readonly ProgramScopedContentRow[]
    ): Promise<ProgramScopedContentRow[]> => {
      if (!termNumber || !scopeKey) return [];
      try {
        const next = await saveProgramScopedContentRowsDiff({
          workspaceId,
          academicYear,
          termNumber: tn,
          gradeId,
          scopeType,
          scopeKey,
          baselineRows,
          snapshotRows: snapshot,
        });
        queryClient.setQueryData(
          schoolKeys.programScopedContentRows(workspaceId, academicYear, tn, gradeId, scopeType, scopeKey),
          next
        );
        rowsOriginScopeIdRef.current = scopeIdentity(workspaceId, academicYear, termNumber, gradeId, scopeType, scopeKey);
        setRows(next);
        setError(null);
        return next;
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        throw e instanceof Error ? e : new Error(String(e));
      }
    },
    [academicYear, gradeId, queryClient, scopeKey, scopeType, termNumber, tn, workspaceId]
  );

  const deleteRowsByIds = React.useCallback(
    async (ids: string[]) => {
      const targetIds = ids.map((v) => v.trim()).filter(Boolean);
      if (!targetIds.length || !termNumber) return;
      try {
        await deleteProgramScopedContentRowsByIds(targetIds);
        setRows((prev) => {
          const next = prev.filter((row) => !targetIds.includes(row.id));
          saveProgramScopedContentRowsCache(
            workspaceId,
            academicYear,
            termNumber,
            gradeId,
            scopeType,
            scopeKey,
            next
          );
          setProgramScopedContentRowsQueryData(
            workspaceId,
            academicYear,
            tn,
            gradeId,
            scopeType,
            scopeKey,
            next
          );
          queryClient.setQueryData(
            schoolKeys.programScopedContentRows(
              workspaceId,
              academicYear,
              tn,
              gradeId,
              scopeType,
              scopeKey
            ),
            next
          );
          return next;
        });
        setError(null);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        throw e instanceof Error ? e : new Error(String(e));
      }
    },
    [academicYear, gradeId, queryClient, scopeKey, scopeType, termNumber, tn, workspaceId]
  );

  return {
    loading,
    error,
    rows,
    upsertRows,
    saveSnapshotRows,
    deleteRowsByIds,
  };
}
